import React from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiClock, FiShield, FiSettings, FiMonitor, FiDatabase, FiGlobe, FiFileText, FiZap } = FiIcons;

const FeaturesPage = () => {
  const features = [
    {
      icon: FiClock,
      title: 'Ultra-Low Latency Execution',
      description: 'Execute trades with <5ms latency across all connected accounts. Our optimized architecture ensures zero missed opportunities.',
      details: [
        'Real-time market data synchronization',
        'Instant order routing and execution',
        'Minimal network overhead',
        'Optimized for high-frequency environments'
      ]
    },
    {
      icon: FiShield,
      title: 'Advanced Risk Management',
      description: 'Comprehensive risk controls to protect your capital across all trading accounts.',
      details: [
        'Per-account position size limits',
        'Equity-based risk calculations',
        'Maximum drawdown protection',
        'Real-time P&L monitoring'
      ]
    },
    {
      icon: FiSettings,
      title: 'Flexible Configuration',
      description: 'Customize NinjaCopia to match your exact trading requirements and preferences.',
      details: [
        'Account-specific lot sizing rules',
        'Symbol filtering and mapping',
        'Custom scaling ratios',
        'Conditional copying rules'
      ]
    },
    {
      icon: FiMonitor,
      title: 'Multi-Platform Support',
      description: 'Compatible with all major NinjaTrader 8 data feeds and broker connections.',
      details: [
        'Rithmic Professional',
        'Continuum Data',
        'ATAS Platform integration',
        'Interactive Brokers support'
      ]
    },
    {
      icon: FiDatabase,
      title: 'Comprehensive Logging',
      description: 'Detailed execution logs and performance analytics for complete transparency.',
      details: [
        'Trade-by-trade execution records',
        'Performance analytics dashboard',
        'Error tracking and reporting',
        'Audit trail for compliance'
      ]
    },
    {
      icon: FiGlobe,
      title: 'Multi-Language Interface',
      description: 'Full support for English, Spanish, and French interfaces and documentation.',
      details: [
        'Localized user interface',
        'Multi-language documentation',
        'Regional support teams',
        'Currency and timezone support'
      ]
    }
  ];

  const systemRequirements = {
    minimum: [
      'Windows 10 64-bit or newer',
      'Intel i5 4th gen or AMD equivalent',
      '8 GB RAM',
      '500 MB available disk space',
      'NinjaTrader 8.0.29.1 or newer',
      'Stable internet connection (10+ Mbps)'
    ],
    recommended: [
      'Windows 11 64-bit',
      'Intel i7 8th gen or AMD Ryzen 5',
      '16 GB RAM',
      '1 GB available SSD space',
      'NinjaTrader 8.1.3.1 or newer',
      'High-speed internet (50+ Mbps)'
    ]
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="Features & Capabilities"
        description="Explore the powerful features of NinjaCopia: <5ms latency, advanced risk management, multi-platform support, and detailed execution logging."
        keywords="NinjaTrader copier features, trade replication latency, risk management trading, prop firm copier"
        url="https://ninjacopia.com/#/features"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-900 dark:to-blue-900/20 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6"
            >
              Powerful Features for Professional Traders
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
            >
              NinjaCopia delivers enterprise-grade trade copying with the reliability and performance that serious traders demand.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700"
              >
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mb-6">
                  <SafeIcon icon={feature.icon} className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                  {feature.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                  {feature.description}
                </p>
                <ul className="space-y-2">
                  {feature.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mr-3"></div>
                      {detail}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* System Requirements */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              System Requirements
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Ensure your system meets these requirements for optimal performance
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <SafeIcon icon={FiMonitor} className="w-6 h-6 text-blue-600 dark:text-blue-400 mr-3" />
                Minimum Requirements
              </h3>
              <ul className="space-y-3">
                {systemRequirements.minimum.map((req, idx) => (
                  <li key={idx} className="flex items-center text-gray-600 dark:text-gray-400">
                    <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mr-3"></div>
                    {req}
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white dark:bg-gray-900 p-8 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center">
                <SafeIcon icon={FiZap} className="w-6 h-6 text-green-600 dark:text-green-400 mr-3" />
                Recommended Requirements
              </h3>
              <ul className="space-y-3">
                {systemRequirements.recommended.map((req, idx) => (
                  <li key={idx} className="flex items-center text-gray-600 dark:text-gray-400">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full mr-3"></div>
                    {req}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Compatibility Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Broker & Data Feed Compatibility
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              NinjaCopia works seamlessly with all major NinjaTrader-compatible brokers and data feeds
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {['Rithmic', 'Continuum', 'ATAS', 'Interactive Brokers'].map((broker, idx) => (
              <div key={broker} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiDatabase} className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{broker}</h3>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Additional broker integrations available upon request. Contact us for custom implementations.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FeaturesPage;